import React, { FC, useState, useEffect } from "react";
import { useStyles } from "./styles";
import { Link } from "react-router-dom";
import constants from "./../../../utils/constants";
import { useSelector, useDispatch } from "react-redux";
import { debounce, Tooltip } from "@material-ui/core";
import { BookClosureHeader } from "./book-closure-header";
import { setSnackBar } from "../../../stores/ui/ui-action";
import { getBookClosureCompanies, toggleCompanyHiddenStatus } from "../../../services/bff-service";
import { GridRenderCellParams, GridColumnGroupingModel } from "@mui/x-data-grid";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { VerticalActionMenu } from "../../common/vertical-action-menu";
import { Parser } from 'html-to-react';
import { CustomDataGrid } from "../../common";
import moment from "moment-timezone";
import { useLinkWithSrc } from "../../../shared/hooks/use-route-navigator";


export const BookClosureDashboard: FC<any> = () => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [pageSize, setPageSize] = useState<number>(50); // Default page size to 50 for company list
  const [companiesByName, setAllCompaniesByName] = useState([]); // Filtered list of companies by Name
  const [hiddenStatusChange, setHiddenStatusChange] = useState(null); // Re-render flag
  const [severitySegment, setSeveritySegment] = useState("HIGH");
  const { userTeamCodes: useUserPortfolioTeamCodes = [] } = user || {};
  const [isApplyTeamCodeFilterIncluded, toggleApplyTeamCodeFilter] = useState<boolean>(false);
  
  const linkWithSrcInstance = useLinkWithSrc();
  

  const handleSeverityLevelChange = (level: string) => {
    setSeveritySegment(level);
  }

  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const fetchCompaniesData = async (config: any) => {
    try {
      const companies = await getBookClosureCompanies(config);
      setCompanies((user?.user_role === "admin") ? companies : companies.filter((company) => !company?.hidden));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if(error.message === 'canceled') return;
      dispatch(
        setSnackBar({
          show: true,
          message: "Error during setup, please refresh and try again momentarily.",
          description: error.message,
          messageType: "error",
        })
      );
    }
  };

  useEffect(() => {
    const abortControllerInstance = new AbortController();
    fetchCompaniesData({ signal: abortControllerInstance.signal });
    // Cancel the long lived in-progress REST API requests when the user left from the component.
    return () => {
      abortControllerInstance.abort();
    }
  }, []);

  useEffect(() => {
    let filteredRows = companies;
    if (severitySegment !== "ANY") {
      filteredRows = companies.filter((row) => row?.segment?.toLowerCase() === severitySegment.toLowerCase());
    }
    if (isApplyTeamCodeFilterIncluded) {
      filteredRows = useUserPortfolioTeamCodes.length ? filteredRows.filter((row) => useUserPortfolioTeamCodes.includes(row?.team_code)) : filteredRows;
    }
    if (!searchText) {
      setAllCompaniesByName(filteredRows);
      return;
    }

    const lowerCaseSearchText = searchText.toLowerCase();
    const finalFilteredRows = filteredRows.filter((row) => row?.legal_entity_name?.toLowerCase().includes(lowerCaseSearchText) || row?.codat_company_id?.includes(lowerCaseSearchText) || row?.cif?.includes(searchText));
    setAllCompaniesByName(finalFilteredRows)

  }, [searchText, companies, severitySegment, isApplyTeamCodeFilterIncluded, useUserPortfolioTeamCodes]);

  const toggleHiddenStatusByCompanyID = (actionType: string, rowData: any) => {
    const isHidden = actionType === "HIDE" ? true : false;
    const { userEmail, userEntityMappingId, companyId, dbPlatform } = rowData;
    setLoading(true);
    const payload = {
      userEmail,
      userEntityMappingId,
      companyId,
      dbPlatform,
      hidden: isHidden,
    };
    const message = isHidden ? "Company hidden." : "Company unhidden.";
    toggleCompanyHiddenStatus(payload)
      .then(() => setHiddenStatusChange(!hiddenStatusChange))
      .then(() => {
        dispatch(
          setSnackBar({
            show: true,
            message,
            messageType: "success",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
          })
        );
      })
      .catch((error) => {
        dispatch(
          setSnackBar({
            show: true,
            message: "Oops! Something went wrong",
            description: error.message,
            messageType: "error",
          })
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'indicators',
      headerName: 'Indicators',
      headerClassName: `${user?.user_role === "admin" ? styles.borderRight : ''} ${styles.borderLeft} ${styles.typographyHeader}`,
      children: [
        { field: 'non_zero_accounts' },
        { field: 'cash_vs_burn_rate' },
        { field: 'bal_sheet_cash_vs_svb_cash' },
        { field: 'last_months_restatement' },
      ],
    },
  ];

  const columns = [
    {
      field: 'legal_entity_name',
      headerName: 'Company Legal Name',
      minWidth: 200,
      flex: 1,
      sortable: true,
      cellClassName: styles.colorBlue,
      headerClassName: `${styles.hideRightSeparator} ${styles.typographyHeaderLeft}`,
      renderCell: (params: GridRenderCellParams) => <Tooltip arrow title={params.value ?? "NA"}>
        <span className={styles.textOverflow}>
          {params?.row?.codat_company_id ? <Link to={linkWithSrcInstance(`/svber/data-management/company-profile/${params?.row?.codat_company_id}`)}> {params.value ?? "NA"}</Link> : params.value ?? "NA"}</span>
      </Tooltip>
    },
    {
      field: 'cif',
      headerName: 'CIF',
      width: 108,
      sortable: false,
      headerClassName: `${styles.hideRightSeparator} ${styles.typographyHeaderLeft}`,
    },
    {
      field: 'projected_closed_date',
      headerName: 'Projected Close Date',
      width: 212,
      sortable: false,
      headerClassName: `${styles.hideRightSeparator} ${styles.typographyHeaderLeft} ${styles.tooltipGap}`,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <Tooltip title={Parser().parse(constants.TOOLTIP_TITLES.BOOK_CLOSURE_PROJECTED_DATE)} placement="bottom" arrow>
            <InfoOutlinedIcon data-testid="tooltip-nonZeroAccount" fontSize="small" color="action"/>
          </Tooltip>
        </>
      ),
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          `${moment.parseZone(params.value as string).format("MMM DD YYYY")}`
        ) : (
          <p>NA</p>
        ),
    },
    {
      field: 'non_zero_accounts',
      headerName: 'Non-Zero Account',
      flex: 1,
      sortable: false,
      headerClassName: `${styles.borderLeft} ${styles.tooltipGap}`,
      cellClassName: styles.borderLeft,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <Tooltip title={Parser().parse(constants.TOOLTIP_TITLES.BOOK_CLOSURE_NON_ZERO_ACC)} placement="bottom" arrow>
            <InfoOutlinedIcon data-testid="tooltip-nonZeroAccount" fontSize="small" color="action" />
          </Tooltip>
        </>
      ),
      renderCell: (params) => {
        const row = params.row;
        const nonZeroAccounts = row?.non_zero_accounts;

        if(!nonZeroAccounts) {
          return (
            <div>
              <span>NA</span>
            </div>
          );
        }

        const variance = nonZeroAccounts?.variance;
        const isHighSegment = nonZeroAccounts.segment?.toLowerCase() === "high";
        return (
          <div>
            <span className={isHighSegment ? styles.colorRed : ''}>
              {`${variance}%`}
            </span>
            &nbsp;vs T2M
          </div>
        )
      }
    },
    {
      field: 'cash_vs_burn_rate',
      headerName: 'Cash vs Burn Rate',
      flex: 1,
      sortable: false,
      headerClassName: styles.tooltipGap,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <Tooltip title={Parser().parse(constants.TOOLTIP_TITLES.BOOK_CLOSURE_BURN_RATE)} placement="bottom" arrow>
            <InfoOutlinedIcon data-testid="tooltip-cash_vs_burn_rate" fontSize="small" color="action" />
          </Tooltip>
        </>
      ),
      renderCell: (params) => {
        const row = params.row;
        const cashVsBurnRate = row?.cash_vs_burn_rate;

        if(!cashVsBurnRate) {
          return (
            <div>
              <span>NA</span>
            </div>
          );
        }

        const variance = cashVsBurnRate?.variance;
        const isHighSegment = cashVsBurnRate.segment?.toLowerCase() === "high";
        return (
          <>
            <span className={isHighSegment ? styles.colorRed : ''}>
              {`${variance}%`}
            </span>
            &nbsp;vs T2M
          </>
        )
      }
    },
    {
      field: 'bal_sheet_cash_vs_svb_cash',
      headerName: 'Bal Sheet Cash vs SVB Cash',
      flex: 1,
      sortable: false,
      headerClassName: styles.tooltipGap,
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <Tooltip title={Parser().parse(constants.TOOLTIP_TITLES.BOOK_CLOSURE_CASH_DIFFERENCE)} placement="bottom" arrow>
            <InfoOutlinedIcon data-testid="tooltip-bal_sheet_cash_vs_svb_cash" fontSize="small" color="action" />
          </Tooltip>
        </>
      ),
      renderCell: (params) => {
        const row = params.row;
        const balSheeetCashVsSVBCash = row?.bal_sheet_cash_vs_svb_cash;

        if(!balSheeetCashVsSVBCash) {
          return (
            <div>
              <span>NA</span>
            </div>
          );
        }

        const variance = balSheeetCashVsSVBCash?.variance;
        const isHighSegment = balSheeetCashVsSVBCash.segment?.toLowerCase() === "high";
        return (
          <>
            <span className={isHighSegment ? styles.colorRed : ''}>
              {`${variance}%`}
            </span>
          </>
        )
      }
    },
    {
      field: 'last_months_restatement',
      headerName: "Last Month's Restatements",
      flex: 1,
      sortable: false,
      headerClassName: `${user?.user_role === "admin" ? styles.borderRight : ''} ${styles.hideRightSeparator} ${styles.tooltipGap}`,
      cellClassName: user?.user_role === "admin" ? styles.borderRightTableCell : '',
      renderHeader: (params) => (
        <>
          <span className={styles.typographyHeader}> {params.colDef.headerName}</span>
          <Tooltip title={Parser().parse(constants.TOOLTIP_TITLES.BOOK_CLOSURE_RESTATEMENT)} placement="bottom" arrow>
            <InfoOutlinedIcon data-testid="tooltip-last_months_restatement" fontSize="small" color="action" />
          </Tooltip>
        </>
      ),
      renderCell: (params) => {
        const row = params.row;
        const lastMonthsRestatement = row?.last_months_restatement;
        if(!lastMonthsRestatement) {
          return (
            <div>
              <span>NA</span>
            </div>
          );
        }

        const line_count = row?.last_months_restatement?.no_of_lines?.[`${row?.last_months_restatement?.segment?.toLowerCase()}_variance`]?.lines;

        const isLowSegment = lastMonthsRestatement.segment?.toLowerCase() === "low";
        const isMediumSegment = lastMonthsRestatement.segment?.toLowerCase() === "medium";
        const isHighSegment = lastMonthsRestatement.segment?.toLowerCase() === "high";

        if (isLowSegment) {
          return <span>0</span>;
        } else if (isMediumSegment) {
          const text = ` lines <${row?.last_months_restatement?.no_of_lines?.high_variance.high_boundary}% change`;
          return (
            <span>
              {line_count} {text}
            </span>
          )
        } else if (isHighSegment) {
          return (
            <>
              <span>
                {line_count} lines&nbsp;
              </span>
              <span className={isHighSegment ? styles.colorRed : ''}>
                {`>${row?.last_months_restatement?.no_of_lines?.high_variance.high_boundary}% `}
              </span>
              <span> &nbsp; change</span>
            </>
          )
        }
      }
    },
    ...(user?.user_role === "admin"
      ? [
        {
          field: "actions",
          headerName: " ",
          width: 100,
          sortable: false,
          headerClassName: styles.hideRightSeparator,
          renderCell: (params: GridRenderCellParams) => <VerticalActionMenu companyIsHidden={params?.row?.hidden} handleHiddenStateChange={(actionType: string) => toggleHiddenStatusByCompanyID(actionType, params?.row)} />,
        }
      ]
      : [])
  ];


  return (
    <>
      <BookClosureHeader
        onSearch={debounce(setSearchText, 300)}
        handleApplyTeamCodeSwitchToggle={toggleApplyTeamCodeFilter}
        handleSeverityLevelChange={handleSeverityLevelChange} />
      <CustomDataGrid columns={columns} rows={companiesByName} loading={isLoading} experimentalFeatures={{ columnGrouping: true }} columnGroupingModel={columnGroupingModel} getRowId={(row: any) => row?.company_id ?? row?.codat_company_id} pageSize={pageSize} onPageSizeChange={handlePageSizeChange} className={styles.tableWrapper} />
    </>
  )
};

